import { Grid } from '@mui/material';
import { isDefAndNotNull } from '@utils/def';
import { FC, useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { StyleTypes } from '../../../Button';
import { ICONS_TYPES } from '../../../Icon';

import { FormFields, FormFieldsWithId } from '../../types';
import { getGridColumnAmount, normalizeDefaultValue } from '../../utils';

import FormFieldRenderer from '../FormFieldRenderer';

import {
  Body,
  ButtonsContainer,
  Container,
  FieldGridItem,
  FieldsContainer,
  FullRowFieldsGrid,
  Label,
  Row,
  RowsContainer,
  StyledAddButton,
  StyledDeleteButton,
} from './styles';
import { Props } from './types';

const FormArrayRenderer: FC<Props> = ({ arrayField }) => {
  const { control } = useFormContext<FormFields>();
  const { fields, append, update, remove } = useFieldArray<FormFields>({
    control,
    name: arrayField.name,
  });
  const watchedFields = useWatch<FormFields>({
    name: arrayField.name,
    control,
  });
  const arrayItems: FormFieldsWithId[] = useMemo(
    () => fields as FormFieldsWithId[],
    [fields],
  );
  const gridColumnAmount = useMemo(
    () => getGridColumnAmount(arrayField?.columnsAmount),
    [arrayField?.columnsAmount],
  );

  const handleAdd = useCallback(() => {
    const newFields: FormFields = {};

    arrayField.properties.forEach((property) => {
      newFields[property.name] = normalizeDefaultValue(property);
    });
    append([newFields as never]);
  }, [append, arrayField.properties]);
  const handleDelete = useCallback(
    (rowIndex: number) => {
      remove(rowIndex);
    },
    [remove],
  );

  return (
    <Grid item xs={12}>
      {arrayField.label && (
        <Label htmlFor={arrayField.name}>{arrayField.label}</Label>
      )}
      <Container>
        <Body>
          <RowsContainer
            columnCount={gridColumnAmount}
            fullRowMode={arrayField.fullRowMode}>
            {arrayItems.map((arrayItem, index) => {
              const fieldValue = (watchedFields as FormFields[])?.[index] || {};

              return (
                <Row key={arrayItem.id}>
                  {arrayField.fullRowMode ? (
                    <FullRowFieldsGrid columnCount={gridColumnAmount}>
                      {arrayField.properties.map((field) => {
                        const span = Math.min(
                          getGridColumnAmount(field.columnsAmount),
                          gridColumnAmount,
                        );

                        return (
                          <FieldGridItem key={field.name} span={span}>
                            <FormFieldRenderer
                              field={{
                                ...field,
                                name: `${arrayField.name}.${index}.${field.name}`,
                                value: fieldValue[field.name],
                              }}
                            />
                          </FieldGridItem>
                        );
                      })}
                    </FullRowFieldsGrid>
                  ) : (
                    <FieldsContainer columnCount={arrayField.properties.length}>
                      {arrayField.properties.map((field) => (
                        <FormFieldRenderer
                          key={field.name}
                          field={{
                            ...field,
                            name: `${arrayField.name}.${index}.${field.name}`,
                            value: fieldValue[field.name],
                          }}
                        />
                      ))}
                    </FieldsContainer>
                  )}

                  {arrayItems.length > arrayField.minRows && (
                    <StyledDeleteButton
                      leftIcon={{ size: 16, type: ICONS_TYPES.Delete }}
                      onClick={() => handleDelete(index)}
                      styleType={StyleTypes.simpleDanger}
                    />
                  )}
                </Row>
              );
            })}
          </RowsContainer>
          <ButtonsContainer>
            {isDefAndNotNull(arrayField.renderActions) ? (
              arrayField.renderActions(
                arrayField.properties,
                fields,
                append,
                update,
                remove,
              )
            ) : (
              <StyledAddButton
                label="Add item"
                leftIcon={{ size: 16, type: ICONS_TYPES.Plus }}
                onClick={handleAdd}
                styleType={StyleTypes.outlined}
              />
            )}
          </ButtonsContainer>
        </Body>
      </Container>
    </Grid>
  );
};

export default FormArrayRenderer;
